import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const useSGMList = (eventId, bookieWeights = "") => {
  const [sgmBets, setSGMBets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Memoize parameters to detect changes
  const memoizedParams = useMemo(() => {
    return {
      eventId,
      bookieWeights,
    };
  }, [eventId, bookieWeights]);

  const fetchSGMBets = useCallback(async () => {
    if (window.membershipLevel !== 30) {
      return;
    }

    setLoading(true);

    try {
      const url =
        window.location.hostname === "localhost"
          ? `./valuebets.json`
          : `${window.location.origin}/wp-json/bonusbank/v1/value-bets/`;

      // Make two API calls, one with evMode on and one with evMode off
      const [evModeOnResponse, evModeOffResponse] = await Promise.all([
        axios.get(url, {
          params: {
            events: [memoizedParams.eventId],
            ...(memoizedParams.bookieWeights
              ? { bookie_weights: memoizedParams.bookieWeights }
              : {}),
          },
          headers: {
            "content-type": "application/json",
            "X-WP-Nonce": window.nonce,
          },
          paramsSerializer: (params) => {
            // Handle the events array parameter correctly
            let queryParams = `events=${params.events.join(",")}`;

            // Add bookie_weights only if present
            if (params.bookie_weights) {
              queryParams += `&bookie_weights=${params.bookie_weights}`;
            }

            return queryParams;
          },
        }),
        axios.get(url, {
          params: { events: [memoizedParams.eventId], non_ev_mode: true },
          headers: {
            "content-type": "application/json",
            "X-WP-Nonce": window.nonce,
          },
          paramsSerializer: (params) => {
            // Handle the events array parameter correctly
            if (params.events) {
              return `events=${params.events.join(",")}&non_ev_mode=${
                params.non_ev_mode
              }`;
            }
            return new URLSearchParams(params).toString();
          },
        }),
      ]);

      const evModeOnBets = Array.isArray(evModeOnResponse.data.value_bets)
        ? evModeOnResponse.data.value_bets
        : Object.keys(evModeOnResponse.data.value_bets).length === 0
        ? []
        : Object.values(evModeOnResponse.data.value_bets);

      const evModeOffBets = Array.isArray(evModeOffResponse.data.value_bets)
        ? evModeOffResponse.data.value_bets
        : Object.keys(evModeOffResponse.data.value_bets).length === 0
        ? []
        : Object.values(evModeOffResponse.data.value_bets);

      // Combine the results
      const combinedBets = [...evModeOnBets, ...evModeOffBets];

      // Process the bets to add the key field and ensure all required fields exist
      const processedBets = combinedBets.map((bet) => {
        const { bet: selection, player, team, line } = bet;
        let key = `${selection}`;
        if (player && player !== selection) key += ` - ${player}`;
        if (team && team !== selection) key += ` - ${team}`;
        if (line && line !== selection) key += ` ${line}`;

        // Ensure all required ratio fields exist
        return {
          ...bet,
          key,
          oddsAveRatio: bet.oddsAveRatio || 1,
          firstSecondRatio: bet.firstSecondRatio || 1,
          oddsFairRatio: bet.oddsFairRatio || 1,
          aveOdds: bet.aveOdds || 0,
        };
      });

      setSGMBets(processedBets);
      setError(null);
    } catch (err) {
      const errorDetails = {
        message:
          err.response?.data?.error?.message ||
          err.message ||
          "An unknown error occurred",
        code: err.response?.data?.error?.code || err.code || "unknown",
        details: err.response?.data?.error?.details || null,
        timestamp: new Date().toISOString(),
        status: err.response?.status,
      };

      console.error("SGM Betting Error:", errorDetails);
      setError(errorDetails);
      setSGMBets([]);
    } finally {
      setLoading(false);
    }
  }, [memoizedParams]);

  // Primary effect - fetch data when parameters change
  useEffect(() => {
    fetchSGMBets();
  }, [fetchSGMBets]);

  // Secondary effect - handle manual refresh
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchSGMBets();
    }
  }, [refreshTrigger, fetchSGMBets]);

  // Memoize filter options for better performance
  const filterOptions = useMemo(() => {
    if (!sgmBets.length) return { bookies: [], markets: [] };

    // Use Set to efficiently get unique values, then convert back to array
    const uniqueBookies = [...new Set(sgmBets.map((bet) => bet.bookie))];
    const uniqueMarkets = [...new Set(sgmBets.map((bet) => bet.market))];

    return {
      bookies: uniqueBookies,
      markets: uniqueMarkets,
    };
  }, [sgmBets]);

  const triggerRefresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  return {
    sgmBets,
    loading,
    error,
    triggerRefresh,
    filterOptions, // Return pre-calculated filter options
  };
};

export default useSGMList;
